import React from "react";

// Shared markdown components for rendering markdown content across the application
// Note: react-markdown v9+ passes a `node` (hast Element) prop to all components.
// We destructure it out to avoid rendering [object Object] as an HTML attribute.
export const MARKDOWN_COMPONENTS = {
  p: ({ node, children, ...props }: any) => {
    // Check if this paragraph only contains an image
    const isImageOnly =
      React.Children.count(children) === 1 &&
      React.Children.toArray(children).every(
        (child: any) => child?.type === "img" || child?.props?.node?.tagName === "img"
      );

    if (isImageOnly) {
      return <p {...props}>{children}</p>;
    }

    return (
      <p {...props} className="mb-2 last:mb-0">
        {children}
      </p>
    );
  },
  h1: ({ node, children, ...props }: any) => (
    <h1 {...props} className="text-2xl font-bold mb-4 mt-6 first:mt-0 border-b pb-2">
      {children}
    </h1>
  ),
  h2: ({ node, children, ...props }: any) => (
    <h2 {...props} className="text-xl font-semibold mb-3 mt-6 border-b pb-2">
      {children}
    </h2>
  ),
  h3: ({ node, children, ...props }: any) => (
    <h3 {...props} className="text-lg font-semibold mb-2 mt-4">
      {children}
    </h3>
  ),
  ul: ({ node, children, ...props }: any) => (
    <ul {...props} className="list-disc list-outside mb-2 space-y-1 pl-5">
      {children}
    </ul>
  ),
  ol: ({ node, children, ...props }: any) => (
    <ol {...props} className="list-decimal list-outside mb-2 space-y-1 pl-5">
      {children}
    </ol>
  ),
  li: ({ node, children, ...props }: any) => <li {...props}>{children}</li>,
  // In react-markdown v9+, inline code renders as <code> and block code renders
  // as <pre><code>. The `inline` prop no longer exists. We style <code> for
  // inline usage here, and handle block code via the `pre` component below.
  code: ({ node, children, ...props }: any) => (
    <code
      {...props}
      className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200"
    >
      {children}
    </code>
  ),
  pre: ({ node, children, ...props }: any) => (
    <pre
      {...props}
      className="bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto mb-2 [&>code]:p-0 [&>code]:bg-transparent [&>code]:rounded-none"
    >
      {children}
    </pre>
  ),
  a: ({ node, children, ...props }: any) => (
    <a
      {...props}
      className="text-blue-600 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  blockquote: ({ node, children, ...props }: any) => (
    <blockquote {...props} className="border-l-4 border-gray-300 pl-4 italic my-2">
      {children}
    </blockquote>
  ),
  table: ({ node, children, ...props }: any) => (
    <div className="overflow-x-auto my-2">
      <table {...props} className="min-w-full divide-y divide-gray-300">
        {children}
      </table>
    </div>
  ),
  thead: ({ node, children, ...props }: any) => (
    <thead {...props} className="bg-gray-50 dark:bg-gray-800">
      {children}
    </thead>
  ),
  tbody: ({ node, children, ...props }: any) => (
    <tbody {...props} className="divide-y divide-gray-200 dark:divide-gray-700">
      {children}
    </tbody>
  ),
  tr: ({ node, children, ...props }: any) => <tr {...props}>{children}</tr>,
  th: ({ node, children, ...props }: any) => (
    <th {...props} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ node, children, ...props }: any) => (
    <td {...props} className="px-3 py-2 text-sm">
      {children}
    </td>
  ),
};
